import { createReducer, on } from '@ngrx/store';

import { OryFlowType } from '../constants';
import * as OryAuthActions from './ory-auth.actions';
import { OryAuthState, initialOryAuthState } from './ory-auth.models';

export const oryAuthReducer = createReducer(
  initialOryAuthState,

  on(
    OryAuthActions.loadSession,
    (state): OryAuthState => ({
      ...state,
      sessionLoading: true,
      sessionError: null,
    }),
  ),

  on(
    OryAuthActions.loadSessionSuccess,
    (state, { session }): OryAuthState => ({
      ...state,
      session,
      sessionLoading: false,
      sessionError: null,
    }),
  ),

  on(
    OryAuthActions.loadSessionFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      session: null,
      sessionLoading: false,
      sessionError: error,
    }),
  ),

  on(
    OryAuthActions.clearSession,
    (state): OryAuthState => ({
      ...state,
      session: null,
      sessionError: null,
    }),
  ),

  on(
    OryAuthActions.startLoginFlow,
    (state, { redirectTo }): OryAuthState => ({
      ...state,
      flowType: OryFlowType.LOGIN,
      loginFlowLoading: true,
      loginFlowError: null,
      redirectTo: redirectTo ?? state.redirectTo,
    }),
  ),

  on(
    OryAuthActions.startLoginFlowSuccess,
    (state, { flowId, csrfToken }): OryAuthState => ({
      ...state,
      flowId,
      csrfToken,
      loginFlowLoading: false,
      loginFlowError: null,
    }),
  ),

  on(
    OryAuthActions.startLoginFlowFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      loginFlowLoading: false,
      loginFlowError: error,
    }),
  ),

  on(
    OryAuthActions.loadLoginFlow,
    (state): OryAuthState => ({
      ...state,
      loginFlowLoading: true,
      loginFlowError: null,
      flowType: OryFlowType.LOGIN,
    }),
  ),

  on(
    OryAuthActions.loadLoginFlowSuccess,
    (state, { flow, csrfToken }): OryAuthState => ({
      ...state,
      loginFlow: flow,
      flowId: flow.id,
      csrfToken,
      loginFlowLoading: false,
      loginFlowError: null,
    }),
  ),

  on(
    OryAuthActions.loadLoginFlowFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      loginFlow: null,
      loginFlowLoading: false,
      loginFlowError: error,
    }),
  ),

  on(
    OryAuthActions.submitLogin,
    (state, { formData }): OryAuthState => ({
      ...state,
      loginFormData: formData,
      isSubmitting: true,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.submitLoginSuccess,
    (state, { session, flow, requiresOtp }): OryAuthState => ({
      ...state,
      session: session || state.session,
      loginFlow: flow || state.loginFlow,
      flowId: requiresOtp ? state.flowId : null,
      csrfToken: requiresOtp ? state.csrfToken : null,
      isSubmitting: false,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.submitLoginFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      isSubmitting: false,
      submitError: error,
    }),
  ),

  on(
    OryAuthActions.setRedirectParams,
    (state, { redirectTo }): OryAuthState => ({
      ...state,
      redirectTo: redirectTo ?? null,
    }),
  ),

  on(
    OryAuthActions.startRegistrationFlow,
    (state, { redirectTo }): OryAuthState => ({
      ...state,
      registrationFlowLoading: true,
      registrationFlowError: null,
      redirectTo: redirectTo ?? state.redirectTo,
      flowType: OryFlowType.REGISTRATION,
    }),
  ),

  on(
    OryAuthActions.startRegistrationFlowSuccess,
    (state, { flowId, csrfToken }): OryAuthState => ({
      ...state,
      flowId,
      csrfToken,
      registrationFlowLoading: false,
      registrationFlowError: null,
    }),
  ),

  on(
    OryAuthActions.startRegistrationFlowFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      registrationFlowLoading: false,
      registrationFlowError: error,
    }),
  ),

  on(
    OryAuthActions.loadRegistrationFlow,
    (state): OryAuthState => ({
      ...state,
      registrationFlowLoading: true,
      registrationFlowError: null,
      flowType: OryFlowType.REGISTRATION,
    }),
  ),

  on(
    OryAuthActions.loadRegistrationFlowSuccess,
    (state, { flow, csrfToken }): OryAuthState => ({
      ...state,
      registrationFlow: flow,
      flowId: flow.id,
      csrfToken,
      registrationFlowLoading: false,
      registrationFlowError: null,
    }),
  ),

  on(
    OryAuthActions.loadRegistrationFlowFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      registrationFlow: null,
      registrationFlowLoading: false,
      registrationFlowError: error,
    }),
  ),

  on(
    OryAuthActions.submitRegistration,
    (state, { formData }): OryAuthState => ({
      ...state,
      registrationFormData: formData,
      isSubmitting: true,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.submitRegistrationSuccess,
    (state, { session, flow, requiresOtp }): OryAuthState => ({
      ...state,
      session: session || state.session,
      registrationFlow: flow || state.registrationFlow,
      flowId: requiresOtp ? state.flowId : null,
      csrfToken: requiresOtp ? state.csrfToken : null,
      isSubmitting: false,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.submitRegistrationFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      isSubmitting: false,
      submitError: error,
    }),
  ),

  on(
    OryAuthActions.submitOtp,
    (state): OryAuthState => ({
      ...state,
      isSubmitting: true,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.submitOtpSuccess,
    (state, { session }): OryAuthState => ({
      ...state,
      session: session || state.session,
      flowId: null,
      csrfToken: null,
      registrationFormData: null, // Clear pending registration data
      isSubmitting: false,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.submitOtpFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      isSubmitting: false,
      submitError: error,
    }),
  ),

  on(
    OryAuthActions.resendOtp,
    (state): OryAuthState => ({
      ...state,
      isResending: true,
      resendError: null,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.resendOtpSuccess,
    (state, { flow }): OryAuthState => ({
      ...state,
      loginFlow: flow && 'forced' in flow ? flow : state.loginFlow,
      registrationFlow:
        flow && 'transient_payload' in flow ? flow : state.registrationFlow,
      isResending: false,
      resendError: null,
      submitError: null,
    }),
  ),

  on(
    OryAuthActions.resendOtpFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      isResending: false,
      resendError: error,
    }),
  ),

  on(
    OryAuthActions.startResendCountdown,
    (state): OryAuthState => ({
      ...state,
      resendCountdown: 60, // 1 minute in seconds
    }),
  ),

  on(
    OryAuthActions.updateResendCountdown,
    (state, { countdown }): OryAuthState => ({
      ...state,
      resendCountdown: countdown,
    }),
  ),

  on(
    OryAuthActions.removeResendCountdown,
    (state): OryAuthState => ({
      ...state,
      resendCountdown: 0,
    }),
  ),

  on(
    OryAuthActions.logout,
    (state): OryAuthState => ({
      ...state,
      logoutLoading: true,
      logoutError: null,
    }),
  ),

  on(
    OryAuthActions.logoutSuccess,
    (): OryAuthState => ({
      ...initialOryAuthState,
    }),
  ),

  on(
    OryAuthActions.logoutFailure,
    (state, { error }): OryAuthState => ({
      ...state,
      logoutLoading: false,
      logoutError: error,
    }),
  ),

  on(
    OryAuthActions.clearErrors,
    (state): OryAuthState => ({
      ...state,
      sessionError: null,
      loginFlowError: null,
      registrationFlowError: null,
      submitError: null,
      resendError: null,
      logoutError: null,
    }),
  ),

  on(
    OryAuthActions.clearFlowState,
    (state): OryAuthState => ({
      ...state,
      flowId: null,
      flowType: null,
      csrfToken: null,
      loginFlow: null,
      registrationFlow: null,
      isSubmitting: false,
      isResending: false,
    }),
  ),

  on(
    OryAuthActions.handleFlowExpired,
    (state, { flowType }): OryAuthState => ({
      ...state,
      isSubmitting: false,
      isResending: false,
      loginFlowLoading: false,
      registrationFlowLoading: false,
      flowId: null,
      csrfToken: null,
      loginFlow: flowType === OryFlowType.LOGIN ? null : state.loginFlow,
      registrationFlow:
        flowType === OryFlowType.REGISTRATION ? null : state.registrationFlow,
      loginFlowError:
        flowType === OryFlowType.LOGIN
          ? { message: 'Login flow expired' }
          : state.loginFlowError,
      registrationFlowError:
        flowType === OryFlowType.REGISTRATION
          ? { message: 'Registration flow expired' }
          : state.registrationFlowError,
    }),
  ),
);
